import React, { useContext, useEffect, useState } from "react";
import UserContext from "../../context/UserContext";
import { useNavigate } from "react-router-dom";
import { useCompanies } from "../../context/CompanyContext";
import {
  getAllContainersWithNoBaseByCompanyId,
  getAllSitesByCompanyId,
  getAllContainersByCompanyId,
  addNewSiteByCompanyId,
  addNewContainerBySiteId,
} from "../../api/locationApi";
import { getAllDevicesWithNoContainerByCompanyId } from "../../api/deviceApi";
import {
  getBasesWithNoContainerAndProbeIdByCompanyId,
  getAllBasesInContainersByCompanyId,
} from "../../api/baseApi";
import ConnectDeviceToContainerButton from "../Shared/ConnectDeviceToContainerButton/ConnectDeviceToContainerButton";
import { FormSelect } from "react-bootstrap";
import "./LocationsPage.scss";
import LocationTable from "./LocationTable/LocationTable";
import ConnectBaseToContainerButton from "../Shared/ConnectBaseToContainerButton/ConnectBaseToContainerButton";
import AddSite from "./AddButtons/AddSite";
import AddContainer from "./AddButtons/AddContainer";
import USER_ROLES from "../../constants/userRoles";
import { toast } from "react-toastify";
import PageHeader from "../Shared/PageHeader/PageHeader";
import useTranslation from "../../hooks/useTranslation";
import validateNewSiteData from "../../helpers/validateNewSiteData";

const LocationsPage = () => {
  const { user } = useContext(UserContext);
  const { companies } = useCompanies(); // <-- Get companies list
  const navigate = useNavigate();
  const { t } = useTranslation("locationsPage");
  const { t: tSettings } = useTranslation("settingsPage");

  // DYNAMIC COMPANY ID STATE (Defaults to user's company)
  const [activeCompanyId, setActiveCompanyId] = useState(user.company_id);

  // SITES AND CONTAINERS STATES
  const [sites, setSites] = useState([]);
  const [containers, setContainers] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [filteredContainers, setFilteredContainers] = useState([]);
  const [containerListForBases, setContainerListForBases] = useState([]);

  // DEVICES STATE
  const [devices, setDevices] = useState([]);

  // BASES STATE
  const [bases, setBases] = useState([]);
  const [baseListForDelete, setBaseListForDelete] = useState([]);

  // TABLE HEADERS AND DATA
  const locationsTableHeaders = {
    siteName: t("siteName"),
    containerName: t("containerName"),
    containertype: t("containerType"),
    edit: t("edit"),
    delete: t("delete"),
  };
  const [locationsTableData, setLocationsTableData] = useState([]);

  // Fetch and refresh table data
  const refreshTableData = async () => {
    try {
      const containersData = await getAllContainersByCompanyId(activeCompanyId);
      setContainers(containersData.data);
      setLocationsTableData(containersData.data);
      return containersData.data; 
    } catch (error) {
      console.error("Error fetching containers: ", error);
      return []; 
    }
  };

  const fetchBasesWithNoContainerAndProbeIdByCompanyId = async () => {
    try {
      const basesData = await getBasesWithNoContainerAndProbeIdByCompanyId(activeCompanyId);
      setBases(basesData);
    } catch (error) {
      console.error("Error fetching bases: ", error);
    }
  };

  const fetchContainersWithNoBase = async () => {
    try {
      const containersData = await getAllContainersWithNoBaseByCompanyId(activeCompanyId);
      setContainerListForBases(containersData.data);
    } catch (error) {
      console.error("Error fetching containers: ", error);
    }
  };

  const fetchBasesInContainers = async () => {
    try {
      const basesData = await getAllBasesInContainersByCompanyId(activeCompanyId);
      setBaseListForDelete(basesData);
    } catch (error) {
      console.error("Error fetching bases: ", error);
    }
  };

  const fetchSitesAndContainers = async () => {
    try {
      const sitesData = await getAllSitesByCompanyId(activeCompanyId);
      setSites(sitesData.data);
      const containersData = await getAllContainersByCompanyId(activeCompanyId);
      setContainers(containersData.data);
      setLocationsTableData(containersData.data);
      setFilteredContainers(containersData.data);
    } catch (error) {
      console.error("Error fetching sites: ", error);
    }
  };

  // Re-fetch all data whenever the activeCompanyId changes
  useEffect(() => {
    fetchSitesAndContainers();
    fetchBasesWithNoContainerAndProbeIdByCompanyId();
    fetchContainersWithNoBase();
    fetchBasesInContainers();
    
    const fetchDevicesByCompanyId = async () => {
      try {
        const devicesData = await getAllDevicesWithNoContainerByCompanyId(activeCompanyId);
        setDevices(devicesData);
      } catch (error) {
        console.error("Error fetching devices: ", error);
      }
    };
    fetchDevicesByCompanyId();
    
    // Reset selected site when changing company
    setSelectedSite("all");
  }, [activeCompanyId]);

  const onContainersUpdate = async () => {
    await refreshTableData(); 
    fetchContainersWithNoBase(); 
    fetchBasesInContainers(); 
  };

  const handleSiteChange = async (e) => {
    const siteName = e.target.value;
    setSelectedSite(siteName);
    try {
      const containersData = await getAllContainersByCompanyId(activeCompanyId);
      if (siteName === "all") {
        setContainers(containersData.data);
      } else {
        setContainers(
          containersData.data.filter(
            (container) => container.sitename === siteName,
          ),
        );
      }
    } catch (error) {
      console.error("Error fetching containers: ", error);
    }
  };

  const handleAddNewSiteClick = async (siteData) => {
    const errors = await validateNewSiteData(siteData, user, tSettings);

    if (Object.keys(errors).length > 0) {
      toast.error(Object.values(errors).join(". "));
      return;
    }

    try {
      await addNewSiteByCompanyId(activeCompanyId, siteData);
      toast.success(t("siteAdded"));
      fetchSitesAndContainers();
    } catch (error) {
      console.error("Error adding new site: ", error);
      toast.error("Hiba történt a telephely hozzáadásakor!");
    }
  };

  const handleAddNewContainerClick = async (containerData) => {
    try {
      await addNewContainerBySiteId(containerData);
      toast.success(t("containerAdded"));
      fetchSitesAndContainers();
    } catch (error) {
      console.error("Error adding new container: ", error);
      toast.error("Hiba történt a tároló hozzáadásakor!");
    }
  };

  return (
    <div className="container locations-page__container navbar-margin">
      <PageHeader
        title={t("location_title")}
        helperTitle={t("location_title")}
        helperBody={t("location_helper")}
      />

      <div className="location-page-content">
        <div className="locations-page__header">
          <div className="locations-page__header__filter__wrapper">
            
            {/* COMPANY SELECTOR FOR SUPER ADMINS */}
            {user.roles === USER_ROLES.SUPER_ADMIN && (
              <div className="locations-page__header__filter">
                <FormSelect
                  className="locations-page__header__filter__select"
                  value={activeCompanyId}
                  onChange={(e) => setActiveCompanyId(e.target.value)}
                >
                  <option value="" disabled>{t("company") || "Company"}</option>
                  {companies.map((company) => (
                    <option key={company.companyid} value={company.companyid}>
                      {company.companyname}
                    </option>
                  ))}
                </FormSelect>
              </div>
            )}

            <div className="locations-page__header__filter">
              <FormSelect
                id="locations-locationstype"
                className="locations-page__header__filter__select"
                value={selectedSite || "all"}
                onChange={handleSiteChange}
              >
                <option value="all">{t("all")}</option>
                {sites.map((site) => (
                  <option key={site._id} value={site.siteid}>
                    {site.sitename}
                  </option>
                ))}
              </FormSelect>
            </div>
            
            {user.roles === USER_ROLES.SUPER_ADMIN ||
            user.roles === USER_ROLES.COMPANY_ADMIN ? (
              <div className="location-page__header__btn__wrapper">
                <div className="location-page__header__connect-btn__wrapper">
                  <ConnectBaseToContainerButton
                    baselist={bases}
                    containerList={containerListForBases}
                    baseListForDelete={baseListForDelete}
                    companyId={activeCompanyId}
                    fetchBasesWithNoContainerAndProbeIdByCompanyId={
                      fetchBasesWithNoContainerAndProbeIdByCompanyId
                    }
                    fetchContainersWithNoBase={fetchContainersWithNoBase}
                    fetchBasesInContainers={fetchBasesInContainers}
                    onContainersUpdate={onContainersUpdate} 
                  />
                  <ConnectDeviceToContainerButton
                    deviceList={devices}
                    containerList={containers}
                    siteList={sites}
                  />
                </div>
                <div className="locations-page__header__add__btn__wrapper">
                  <AddSite handleAddNewSiteClick={handleAddNewSiteClick} />
                  <AddContainer
                    handleAddNewContainerClick={handleAddNewContainerClick}
                    siteList={sites}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>
        
        <LocationTable
          tableHeader={locationsTableHeaders}
          tabledata={containers}
          selectedSite={selectedSite}
          refreshTableData={refreshTableData}
        />
      </div>
    </div>
  );
};

export default LocationsPage;